import {
  ChangeEvent,
  Dispatch,
  FC,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { css } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useTranslation } from 'react-i18next'
import CartWidget, { CartWidgetVariant } from '@/components/CartWidget'
import { CartBody } from '@/components/CartWidget/CartBody'
import { CartHeader } from '@/components/CartWidget/CartHeader'
import ContactInfo from '@/components/ContactInfo'
import EditItemModal from '@/components/EditItemModal'
import EditItemModalWithNestedMods from '@/components/EditItemModalWithNestedMods'
import Footer from '@/components/Footer'
import GuestInfo from '@/components/GuestInfo'
import Header from '@/components/Header'
import LocationDetails from '@/components/LocationDetails'
import OrderMethodSelector from '@/components/OrderMethodSelector'
import PickupOnlyReachLimitModal from '@/components/OrderThrotting/PickupOnlyReachLimitModal'
import RescheduleModal from '@/components/OrderThrotting/RescheduleModal'
import PaymentDetails from '@/components/PaymentDetails'
import PopupModalTemplate from '@/components/PopupModalTemplate'
import ProcessingModal from '@/components/ProcessingModal'
import PulsateLoader from '@/components/PulsateLoader'
import VenueClosedModal from '@/components/VenueClosedModal'
import publicConfig from '@/config/public'
import {
  FieldTypes,
  FormField,
  FormFields,
  MenuSource,
  OperationalMenu,
} from '@/libs/helpers/adapters'
import {
  CardCountry,
  CartUpdateRequest,
  GiftCard,
  GiftCardRequest,
  GuestInformation,
  Loyalty,
  LoyaltySession,
  Order,
  OrderPayment,
  OrderRequest,
  PaymentMethod,
  ScheduleTimeslotWithReason,
  ServiceChargeType,
  StreetAddressState,
  TBPaymentType,
  TransactionSetupResponse,
  UpdateMeRequest,
  evaluateRewards,
  getSchedule,
  getVenue,
  requestOrder,
  transactionSetup,
  updateMe,
} from '@/libs/helpers/apiClient'
import { buildAssetsPath } from '@/libs/helpers/assets'
import { composeCartError } from '@/libs/helpers/composeCartError'
import {
  AdvanceDiscountErrorCode,
  LoyaltyRewardErrorCode,
  ScheduledOrderErrorCode,
  StaleCartErrorCode,
} from '@/libs/helpers/errorMapper'
import { parseRequestError } from '@/libs/helpers/error_handler'
import { formatAddress, formatPhone } from '@/libs/helpers/formatters'
import { useCookies } from '@/libs/helpers/hooks/useCookies'
import { useDebounce } from '@/libs/helpers/hooks/useDebounce'
import EditItemProviderWithNestMods from '@/libs/helpers/hooks/useMenuItem'
import { usePayrixSession } from '@/libs/helpers/hooks/usePayrixSession'
import { useWindowSize } from '@/libs/helpers/hooks/useWindowSize'
import { TokenizeError, tokenizeErrorMessage } from '@/libs/helpers/tokenizeErrorMessage'
import {
  CartState,
  DeliveryFleet,
  HeaderVariant,
  OrderItem,
  OrderMethod,
  OrderProvider,
  constructGTMSession,
  dehydrateCartItems,
  getCurrencyByCountry,
  getOrderMethodSettings,
  mapItemsToCartRequest,
  trimAllWhitespace,
} from '@/libs/helpers/utils'
import { validatePhoneNumber, validatePostalCode, validateZipCode } from '@/libs/helpers/validators'
import {
  ErrorPageProps,
  FeatureFlagProps,
  SSRGlobalProps,
} from '@/libs/helpers/withServerSidePropsDecorator'
import { useConfiguration } from '@/providers/configuration/ConfigurationContext'
import { useError } from '@/providers/error'
import { OrderingAddress } from '@/providers/googleMaps'
import { useModal } from '@/providers/modal'
import { useUserLanguage } from '@/providers/userLanguage'
import {
  Button,
  ButtonVariants,
  Divider,
  Grid,
  InputVariant,
  LoadScreen,
  SvgIcon,
  Text,
  TextField,
  formStyles,
} from '@/tbui'
import DefaultThemeProvider from '@/tbui/DefaultThemeProvider'
import { OrderTime } from '../OrderMethodSelector/ScheduledOrderSelector'

const ORDER_NOTES_MAX_LENGTH = 255

export interface LoyaltyUser {
  accountType: string
  accountID: string
  firstName: string
  lastName: string
  phone: string
  email: string
  credit: string
  points: string
  giftCards: GiftCard[]
}

export interface LoyaltySessionState extends LoyaltySession {
  isLoyaltySessionLoading: boolean
}

/** Template/Layout Styles */
const checkoutTemplateStyles = (theme: Theme): string => css`
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
  grid-template-areas:
    'header header header header'
    'main main main main'
    'footer footer footer footer';
  display: grid;
  width: 100%;
  @media (max-width: ${theme.breakpoints.MD}) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, auto);
    grid-template-areas:
      'header'
      'main'
      'footer';
  }
`

const checkoutPageBodyLayout = (theme: Theme): string => css`
  display: grid;
  grid-area: main;
  margin: 0 auto;
  width: 100%;
  grid-template-areas:
    'content sidebar'
    'content sidebar';
  grid-template-columns: 1fr 415px;
  max-width: ${theme.shape.CONTAINER};
  grid-template-rows: auto;

  @media (max-width: ${theme.breakpoints.SM}) {
    grid-template-rows: repeat(3, auto);
    grid-template-columns: 1fr;
    grid-template-areas:
      'content'
      'sidebar';
  }
`

const contentStyles = (theme: Theme): string => css`
  display: grid;
  grid-area: content;
  padding: 44px 24px;
  gap: 1.5rem;
  // 512px = Header + Footer
  min-height: calc(100vh - 512px);

  @media (min-width: ${theme.breakpoints.SM}) {
    max-width: 670px;
  }
`

const venueOrderingStyles = css`
  display: grid;
  grid-area: venueOrdering;
  margin-top: 1.5rem;
  gap: 1.5rem;
`

const formWrapperStyles = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'sidebar'
    'payment'
    'contact';
`

const paymentSectionStyles = css`
  display: grid;
  grid-area: payment;
`

const cartBodyStyles = css`
  grid-area: sidebar;
`

const cartWidgetSectionStyles = css`
  display: grid;
  grid-area: sidebar;
`

const contactSectionStyles = css`
  grid-area: contact;
`

const venueInactiveSection = css`
  display: grid;
  padding: 100px 24px;
`

const StyledButton = styled(Button)`
  width: 11%;
  @media (max-width: ${({ theme }) => theme.breakpoints.MD}) {
    width: 20%;
  }
  text-align: left;
`

const StyledText = styled(Text)`
  margin-top: 6px;
`

const StyledSvgIcon = styled(SvgIcon)`
  position: relative;
  top: 4px;
`

const dividerStyles = css`
  margin: 20px 0;
`

const stickyPlaceOrderButtonStyles = (theme: Theme): string => css`
display: none;
@media (max-width: ${theme.breakpoints.SM}) {
  background: ${theme.palette.WHITE};
  box-shadow: ${theme.shape.SHADOW};
  bottom: 0;
  transition: 0.25s ease;
  padding: 1rem;
  position: fixed;
  display: grid;
  z-index: 1;
  right: 0;
  left: 0;
`

interface ICheckoutProps {
  globals: SSRGlobalProps
  featureFlags: FeatureFlagProps
  giftCards: GiftCard[]
  paymentMethods: PaymentMethod[]
  venueCountry: string
  menuSource: MenuSource
  operationalMenu: OperationalMenu
  error: ErrorPageProps['error']
  setProcessedGuestOrder: Dispatch<SetStateAction<Order | null>>
}

type MeState = {
  loyaltySession: LoyaltySessionState
  cart: CartState
}

// eslint-disable-next-line complexity
const Checkout: FC<ICheckoutProps> = ({
  globals: {
    venue,
    initialCart,
    loyalty,
    loyaltyConfig,
    deliveryAddresses,
    loyaltySession: initialLoyaltySession,
    profile,
    guest,
    tbPaymentType,
    marketingConsent,
    config: { hasAdvancedDiscounts, shouldShowNestedMods },
  },
  featureFlags: { hasOODevFF },
  giftCards,
  paymentMethods,
  menuSource,
  operationalMenu,
  error,
  setProcessedGuestOrder,
}) => {
  // HOOKS
  const theme = useTheme()
  const { setRedirectURLCookie } = useCookies()
  const { openModal, closeModal } = useModal()
  const { userLanguage } = useUserLanguage()
  const { userSession } = useConfiguration()
  const router = useRouter()
  const orderMethod =
    initialCart.orderMethod ?? OrderMethod[router.query.orderMethod as keyof typeof OrderMethod]
  const venueXRefID = venue?.venueXRefID as string
  const { setModalError } = useError()
  const { t } = useTranslation()
  const windowSize = useWindowSize()

  // UI STATE
  const [digitalGiftCardInputError, setDigitalGiftCardInputError] = useState<string | null>(null)
  const [promoCodeInputError, setPromoCodeInputError] = useState<string | null>(null)
  // FORM STATE
  const [isScheduled, setIsScheduled] = useState<boolean>(false)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null)
  const [validPostalOrZipCode, setValidPostalOrZipCode] = useState<boolean>(true)
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false)
  const [postalOrZipCode, setPostalOrZipCode] = useState<string>('')
  const [cardCountry, setCardCountry] = useState<CardCountry>(
    venue?.venueCountry === CardCountry.US ? CardCountry.US : CardCountry.CA
  )
  const [validPhoneNumber, setValidPhoneNumber] = useState<boolean>(true)
  const [phoneNumber, setPhoneNumber] = useState<string>(() => {
    const consumerPhone = profile?.phone
    if (consumerPhone != null && consumerPhone !== '') {
      // Removes the international digit
      const cleanPhoneNumber = consumerPhone.replace('+1', '')
      return formatPhone(cleanPhoneNumber)
    }
    return ''
  })
  const [tipAmount, setTipAmount] = useState<string>('0.00')
  const applyTip = paymentMethod === PaymentMethod.ONLINE
  const [orderNotes, setOrderNotes] = useState<string>('')
  const [wePay, setWePay] = useState<WePayIframe | null>(null)
  const [sendSMS, setSendSMS] = useState<boolean>(false)
  const [includeCutlery, setIncludeCutlery] = useState<boolean>(false)
  const [saveDeliveryAddress, setSaveDeliveryAddress] = useState<boolean>(false)
  const paymentDetailsRef = useRef<HTMLDivElement | null>(null)
  const phoneNumberRef = useRef<HTMLDivElement | null>(null)
  const [validBillingAddress, setValidBillingAddress] = useState<boolean>(true)
  const [streetAddress, setStreetAddress] = useState<StreetAddressState | null>(null)
  const [marketingEmailConsent, setMarketingEmailConsent] = useState<boolean>(false)
  const {
    payrixSDKLoaded,
    payrixSession,
    getPayrixSessionCurrency,
    payrixScriptRef,
    payrixSessionToken,
  } = usePayrixSession(
    tbPaymentType === TBPaymentType.PAYRIX ? venueXRefID : undefined,
    venue?.venueIsTest && hasOODevFF
  )
  const payrixSessionCurrency = useMemo(
    () => getPayrixSessionCurrency(payrixSession),
    [getPayrixSessionCurrency, payrixSession]
  )

  const [{ cart, loyaltySession }, setMe] = useState<MeState>({
    cart: {
      ...initialCart,
      isCartLoading: false,
    },
    loyaltySession: {
      ...initialLoyaltySession,
      isLoyaltySessionLoading: false,
    },
  })

  const prevCartLoyaltyStateRef = useRef<{
    items: OrderItem[]
    isLoyaltyEnabled: boolean
  }>()
  const [userRewards, setUserRewards] = useState<Loyalty | null>(loyalty ?? null)
  const [guestInfo, setGuestInfo] = useState<GuestInformation | null>(guest)

  // CONST
  const offersLoyaltyRewards =
    (loyaltyConfig?.config?.loyaltyEnabled || loyaltyConfig?.config?.marketingEnabled) ?? false
  const consumerXRefID = profile != null ? profile.consumerXRefID : null

  const isPickup = orderMethod === OrderMethod.pickup
  const isDelivery = orderMethod === OrderMethod.delivery
  const isAddressRequired = (isDelivery && cart.orderingAddress == null) ?? false
  const showMarketingConsent =
    !!venue &&
    venue.parentGroupName != null &&
    !!(
      profile &&
      (!marketingConsent || !marketingConsent.emailConsent) &&
      loyaltyConfig.config &&
      (loyaltyConfig.config.marketingEnabled ||
        loyaltyConfig.config.loyaltyEnabled ||
        loyaltyConfig.config.giftCardsEnabled)
    )
  // USEFUL FLAGS
  const isOnlinePayment = paymentMethod === PaymentMethod.ONLINE
  const isPickupAvailable = (venue?.venueOrderingMethods ?? []).some(
    (venueOrderingMethod) => venueOrderingMethod.orderMethod === OrderMethod.pickup
  )
  const isDeliveryAvailable = (venue?.venueOrderingMethods ?? []).some(
    (venueOrderingMethod) => venueOrderingMethod.orderMethod === OrderMethod.delivery
  )

  const isCheckoutPage = router.pathname.includes('/checkout')
  let isPaymentRedirectNoteVisible = false
  isPaymentRedirectNoteVisible =
    paymentMethod === PaymentMethod.ONLINE &&
    isCheckoutPage &&
    tbPaymentType === TBPaymentType.WORLDPAY
  const isCartAvailable =
    (venue?.active ?? true) &&
    ((isDelivery && isDeliveryAvailable) || (isPickup && isPickupAvailable))
  const showOnlinePayment = cart.bill.outstandingAmount !== '0.00' ?? false
  const orderMethodSettings = getOrderMethodSettings(
    venue?.venueOrderingMethods ?? [],
    orderMethod,
    cart.delivery
  )
  const {
    waitTime,
    isSchedulingAvailable,
    isASAPAvailable,
    nextAvailable,
    orderThrottlingLimitReached,
  } = orderMethodSettings
  // nextAvailable is used for scheduling, which accounts for wait time
  // to calculate re-opening time, we must remove wait time from nextAvailable
  const venueReopensAt =
    nextAvailable != null
      ? DateTime.fromISO(nextAvailable).minus({ minutes: waitTime }).toString()
      : null

  // FORM DEFAULTS
  const initialForm: FormFields = useMemo(() => {
    const handlePostalOrZipCodeValidation = (code: string): boolean => {
      let validationResult = false
      switch (tbPaymentType) {
        case TBPaymentType.WORLDPAY:
          validationResult = code.length > 0
          break

        case TBPaymentType.PAYRIX:
          validationResult = validateZipCode(code) || validatePostalCode(code)
          break

        case TBPaymentType.WEPAY:
          validationResult = validatePostalCode(code)
      }

      return validationResult
    }

    return {
      cardCountry: {
        value: '',
        validate: (country): boolean => Object.keys(CardCountry).includes(country),
        type: FieldTypes.TEXT_INPUT,
      },
      postalOrZipCode: {
        value: '',
        validate: handlePostalOrZipCodeValidation,
        type: FieldTypes.TEXT_INPUT,
      },
      paymentMethod: {
        value: '',
        validate: (pmtMethod): boolean => Object.keys(PaymentMethod).includes(pmtMethod),
        type: FieldTypes.TEXT_INPUT,
      },
      tipAmount: {
        value: '0.00',
        validate: (tip: string): boolean => /^\d+\.\d{2}$/.test(tip),
        type: FieldTypes.TEXT_INPUT,
      },
      sendSMS: { value: false, type: FieldTypes.CHECKBOX },
      phoneNumber: {
        value: '',
        validate: validatePhoneNumber,
        type: FieldTypes.TEXT_INPUT,
      },
      billingAddress: {
        value: '',
        validate: (address: string | undefined) => {
          return address != null && address.length > 0
        },
        type: FieldTypes.TEXT_INPUT,
      },
    }
  }, [tbPaymentType])

  const [formFields, setFormFields] = useState<FormFields>(initialForm)

  const handleUpdateMe = async ({
    cart: updateCartRequest,
    giftCardCode,
    guestInformation,
    paymentMethodData,
    keepModalOpen = false,
    promoCodes,
  }: {
    cart: Partial<CartUpdateRequest>
    giftCardCode?: string | null
    guestInformation?: GuestInformation
    paymentMethodData?: PaymentMethod
    keepModalOpen?: boolean
    promoCodes?: string[]
  }): Promise<void> => {
    setPromoCodeInputError(null)

    setMe((previousState) => ({
      ...previousState,
      cart: {
        ...previousState.cart,
        isCartLoading: true,
      },
    }))

    const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

    try {
      const { cart: updatedCart } = await updateMe(venueXRefID, {
        cart: {
          ...updateCartRequest,
          items: (updateCartRequest.items ?? []).map(mapItemsToCartRequest),
          orderMethod: updateCartRequest.orderMethod ?? orderMethod,
          tipAmount: applyTip ? updateCartRequest.tipAmount : undefined,
        },
        giftCardCode,
        guest: guestInformation,
        promoCodes: promoCodes ?? prevPromoCodes,
      })
      setMe((previousState) => ({
        ...previousState,
        cart: { ...updatedCart, isCartLoading: false },
      }))

      if (updatedCart.orderMethod && updatedCart.orderMethod !== orderMethod) {
        await router.replace(
          '/[orderMethod]/[venueXRefID]/checkout',
          `/${updatedCart.orderMethod.toLowerCase()}/${venueXRefID}/checkout`,
          { scroll: false }
        )
      }
      if (!keepModalOpen) {
        closeModal()
      }

      if (venue) {
        // Set data for GTM
        window.dataLayer?.push({
          event: 'session',
          ...constructGTMSession(
            venue,
            operationalMenu,
            profile,
            guest,
            updatedCart,
            paymentMethodData
          ),
        })
      }

      if (hasAdvancedDiscounts) {
        if (updatedCart.discountDetails && updatedCart.discountDetails.promoCodeErrors.length > 0) {
          setModalError(AdvanceDiscountErrorCode.PromoCodeMissing)
        }
      }
    } catch (updateCartError) {
      const { code } = parseRequestError(updateCartError)
      const isPromoCodeError = Object.values(AdvanceDiscountErrorCode).some(
        (errorCode) => errorCode === code
      )
      if (isPromoCodeError) {
        switch (code) {
          case AdvanceDiscountErrorCode.PromoCodeInvalid:
            setPromoCodeInputError(t('error.message_err_discount_promo_code_invalid'))
            break
          case AdvanceDiscountErrorCode.PromoCodeExpired:
            setPromoCodeInputError(t('error.message_err_discount_promo_code_expired'))
            break
          case AdvanceDiscountErrorCode.PromoCodeDuplicated:
            setPromoCodeInputError(t('error.message_err_discount_promo_code_duplicated'))
            break
          case AdvanceDiscountErrorCode.MinSpendNotMeet:
            setPromoCodeInputError(t('error.message_err_discount_does_not_meet_minimum_spend'))
            break
          case AdvanceDiscountErrorCode.MaxSpendNotMeet:
            setPromoCodeInputError(t('error.message_err_discount_over_maximum_spend'))
            break
          case AdvanceDiscountErrorCode.DiscountMissingBogoBuy:
          case AdvanceDiscountErrorCode.DiscountMissingBogoGet:
          case AdvanceDiscountErrorCode.DiscountBogoDoesNotMeetMinimumQuantityPurchase:
            setPromoCodeInputError(t('error.message_err_discount_missing_bogo_buy'))
            break
          case AdvanceDiscountErrorCode.DiscountOutsideOfSchedule:
            setPromoCodeInputError(t('error.message_err_discount_outside_of_schedule'))
            break
          case AdvanceDiscountErrorCode.FixedDiscountAlreadyApplied:
            setPromoCodeInputError(t('error.message_err_discount_fixed_discount_already_applied'))
            break
          case AdvanceDiscountErrorCode.BillDiscountAllItemsExcluded:
            setPromoCodeInputError(t('error.message_err_discount_bill_discount_all_items_excluded'))
            break
          case AdvanceDiscountErrorCode.PromoCodeNotYetActive:
            setPromoCodeInputError(t('error.message_err_discount_promo_code_not_yet_active'))
            break
          case AdvanceDiscountErrorCode.FixedDiscountHigherThanPrice:
            setPromoCodeInputError(t('error.message_err_discount_fixed_discount_higher_than_price'))
            break
          default:
            setPromoCodeInputError(t('error.message_err_discount_promo_code_invalid'))
            break
        }
        router.replace(router.asPath, undefined, {
          scroll: false,
        })
      }
      composeCartError(updateCartError, {
        onLoyaltyError: () => {
          // lty reward, and cart item errors can trigger a change in the backend
          // so we should trigger a router.replace to get the latest the server data
          setModalError(code)
          router.replace(router.asPath, undefined, {
            scroll: false,
          })
        },

        onStaleError: () => {
          setModalError(StaleCartErrorCode.MODIFIER_GENERIC)
          router.replace(router.asPath, undefined, {
            scroll: false,
          })
        },
        onOrderingAddressError: () => {
          setModalError(code, () => {
            // set provider state back to what it was and remove loading
            setMe((previousState) => ({
              ...previousState,
              cart: {
                ...previousState.cart,
                orderingAddress: undefined,
                isCartLoading: false,
              },
            }))
          })
        },
      })
    }
  }

  const handleOpenOrderMethodSelectorModal = async (e?: MouseEvent): Promise<void> => {
    e?.preventDefault()
    if (venue == null) {
      return
    }

    // Refresh venue OT state
    const { venue: newVenueData } = await getVenue(venueXRefID, {
      params: { orderMethod },
    })
    openModal(
      <OrderMethodSelector
        menuSource={menuSource}
        venue={{ ...venue, venueOrderingMethods: newVenueData.venueOrderingMethods }}
        savedAddresses={deliveryAddresses}
        showEditDetails={!isAddressRequired}
        cart={cart}
        onUpdateMe={handleUpdateMe}
      />,
      {
        forceSubmit: isAddressRequired,
        scrollBehavior: 'SCROLL',
      }
    )
  }

  const recoverCartOnError: () => void = useCallback(() => {
    composeCartError(error, {
      onStaleError: () => {
        // set cart to initial state
        setMe((previousState) => ({
          ...previousState,
          cart: {
            items: [],
            orderMethod: OrderMethod.pickup,
            isCartLoading: true,
            bill: {
              subtotal: '0.00',
              totalAfterTax: '0.00',
              grandTotal: '0.00',
              totalTax: '0.00',
              isEstimated: false,
              isInclusive: false,
              serviceCharges: [],
            },
            discountDetails: {
              billTotalDiscounts: [],
              orderItemDiscounts: {},
              promoCodes: [],
              promoCodeErrors: [],
            },
          },
        }))
      },
      onLoyaltyError: () => {
        setMe({
          cart: {
            ...initialCart,
            isCartLoading: true,
          },
          loyaltySession: {
            ...initialLoyaltySession,
            isLoyaltySessionLoading: true,
          },
        })
      },
    })
  }, [error, initialCart, initialLoyaltySession])

  const getScheduleForOrder = async (
    newOrderMethod: OrderMethod,
    newOrderDate: string,
    newOrderingAddress: OrderingAddress | undefined
  ): Promise<ScheduleTimeslotWithReason[]> => {
    if (venue == null) {
      return []
    }

    try {
      const schedule = await getSchedule(venue.venueXRefID, {
        orderMethod: newOrderMethod,
        orderDate: newOrderDate,
        orderingAddress: newOrderMethod === OrderMethod.delivery ? newOrderingAddress : undefined,
      })

      return schedule.schedulingTimeslots
    } catch (err) {
      datadogRum.addError(err)
      setModalError(err)
    }

    return []
  }

  const getNextSchedulingTime = async (): Promise<DateTime | null> => {
    const scheduledFor = cart.scheduledFor
    if (!scheduledFor) {
      return null
    }
    const scheduledForDate = DateTime.fromISO(scheduledFor)
    if (!scheduledForDate.isValid) {
      return null
    }
    const scheduledOrderThrottlingTime = scheduledForDate
      .minus({ minutes: scheduledForDate.minute % 15 })
      .set({ second: 0, millisecond: 0 })
    const nextTime = scheduledOrderThrottlingTime.plus({ minute: 15 })
    const schedulingTimeslots = await getScheduleForOrder(
      orderMethod,
      scheduledForDate.toISODate() ?? '',
      cart.orderingAddress
    )
    if (
      schedulingTimeslots
        .filter(({ available }) => available)
        .map(({ time }) => time)
        .includes(
          nextTime.toUTC().toISOTime({ includeOffset: false, suppressMilliseconds: true }) ?? ''
        )
    ) {
      return nextTime
    }
    return null
  }

  const handleSetStreetAddress = (value: StreetAddressState | null): void => {
    setStreetAddress(value)
    if (tbPaymentType === TBPaymentType.PAYRIX && value !== null) {
      setCardCountry(value.country as CardCountry)
    }
  }

  const handleSelectReward = async (selectedReward: string | null): Promise<void> => {
    if (consumerXRefID == null) {
      return
    }
    const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

    const cartRequest: CartUpdateRequest = {
      orderMethod: cart.orderMethod ?? OrderMethod.pickup,
      items: (cart.items ?? []).map(mapItemsToCartRequest),
      orderingAddress: cart.orderMethod !== OrderMethod.delivery ? undefined : cart.orderingAddress,
      scheduledFor: cart.scheduledFor,
      tipAmount: applyTip ? tipAmount : undefined,
    }

    try {
      setMe((previousState) => ({
        loyaltySession: {
          ...previousState.loyaltySession,
          isLoyaltySessionLoading: true,
        },
        cart: { ...previousState.cart, isCartLoading: true },
      }))

      const me = await updateMe(venueXRefID, {
        cart: cartRequest,
        selectedRewardXRefID: selectedReward,
        promoCodes: prevPromoCodes,
      })

      setMe({
        loyaltySession: {
          ...me.loyaltySession,
          isLoyaltySessionLoading: false,
        },
        cart: {
          ...me.cart,
          isCartLoading: false,
        },
      })
    } catch (selectRewardError) {
      composeCartError(selectRewardError, {
        onLoyaltyError: () => {
          setModalError(selectRewardError)
          router.replace(router.asPath, undefined, {
            scroll: false,
          })
        },
      })
    }

    datadogRum.addAction('update-cart-with-reward', {
      items: cart.items,
      scheduledFor: cart.scheduledFor,
    })
  }

  const handleUserRewards = async (): Promise<void> => {
    if (consumerXRefID == null) {
      return
    }
    try {
      const evalRewardsRequest = {
        orderMethod: cart.orderMethod,
        items: cart.items.map(mapItemsToCartRequest),
        orderingAddress: cart.orderingAddress,
        scheduledFor: cart.scheduledFor,
      }

      const { loyalty: updatedLoyalty } = await evaluateRewards(
        venueXRefID,
        consumerXRefID,
        evalRewardsRequest
      )
      setUserRewards((previousState) =>
        updatedLoyalty != null
          ? {
              ...previousState,
              ...updatedLoyalty,
            }
          : null
      )
      setMe((previousState) => ({
        ...previousState,
        loyaltySession: {
          ...previousState.loyaltySession,
          loyaltyTransactionXRefID: updatedLoyalty?.transactionXRefID,
        },
      }))
    } catch (userRewardError) {
      setModalError(userRewardError)
    }
  }

  const handleClearLoyalty = (): void => {
    setUserRewards(null)
    setMe((previousState) => ({
      ...previousState,
      loyaltySession: {
        ...previousState.loyaltySession,
        selectedRewardXRefID: null,
      },
    }))
  }

  const handleCartItemsChange = async (items: OrderItem[]): Promise<void> => {
    await handleUpdateMe({
      cart: {
        items,
        scheduledFor: cart?.scheduledFor,
        orderingAddress: cart?.orderingAddress,
        orderMethod: cart?.orderMethod,
        tipAmount: applyTip ? tipAmount : undefined,
      },
    })
  }

  // ENFORCES USER TO BE LOGGED IN OR BE A GUEST TO SEE CHECKOUT
  useEffect(() => {
    if (
      userSession.isInitialized &&
      !userSession.isSignedIn &&
      venueXRefID &&
      !venue?.guestCheckoutEnabled
    ) {
      setRedirectURLCookie({
        url: '/[orderMethod]/[venueXRefID]/checkout',
        as: `/${orderMethod.toLowerCase()}/${venueXRefID}/checkout`,
      })
      router.push('/api/consumer-frontend/auth/azure-b2c/login?p=B2C_1A_signup_signin')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, orderMethod, venueXRefID])

  // eslint-disable-next-line complexity
  useEffect(() => {
    // REDIRECTS THE USER TO THE MENU IF CART IS EMPTY
    if (
      orderMethod &&
      venueXRefID &&
      !cart.isCartLoading &&
      cart.items.length === 0 &&
      error == null
    ) {
      router.replace(
        '/[orderMethod]/[venueXRefID]/menu',
        `/${orderMethod.toLowerCase()}/${venueXRefID}/menu`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderMethod, venueXRefID, cart])

  useEffect(() => {
    // SET IS ORDER SCHEDULED - LOCAL STATE
    if (!cart.isCartLoading) {
      setIsScheduled(cart.scheduledFor != null && isSchedulingAvailable)
    }
  }, [cart, isSchedulingAvailable])

  useEffect(() => {
    if (error != null) {
      recoverCartOnError()
    }
  }, [error, recoverCartOnError])

  // temporarily adding both useEffect hooks to make sure the states are up to date
  // TODO: remove both hooks below when removing all of router.replace calls (OO-2983)
  useEffect(() => {
    setUserRewards((previousState) =>
      previousState != null
        ? {
            ...previousState,
            ...loyalty,
          }
        : null
    )
  }, [loyalty])

  useEffect(() => {
    setMe((previousState) => ({
      cart: {
        ...previousState.cart,
        ...initialCart,
        isCartLoading: false,
      },
      loyaltySession: {
        ...previousState.loyaltySession,
        ...initialLoyaltySession,
        isLoyaltySessionLoading: false,
      },
    }))
  }, [initialLoyaltySession, initialCart])

  // VALIDATES FORM FIELDS AT EVERY FIELD CHANGE
  useEffect(() => {
    const {
      paymentMethod: pmtMethod,
      phoneNumber: phoneNum,
      cardCountry: cCountry,
      tipAmount: tipAmt,
      billingAddress: billingAddress,
    } = initialForm

    let onlinePaymentInfo: FormFields = {}
    let contactInfo: FormFields = {}

    if (isOnlinePayment) {
      const isCanadianCard = cardCountry === CardCountry.CA
      const validatePostalOrZipCode = (code: string): boolean => {
        if (tbPaymentType === TBPaymentType.WORLDPAY) {
          return code.length > 0
        } else if (tbPaymentType === TBPaymentType.PAYRIX) {
          return validateZipCode(code) || validatePostalCode(code)
        }

        if (isCanadianCard) {
          return validatePostalCode(code)
        }

        return validateZipCode(code)
      }

      // only validate fields if online payment selected
      onlinePaymentInfo = {
        cardCountry: {
          validate: cCountry.validate,
          value: cardCountry,
          type: FieldTypes.TEXT_INPUT,
        } as FormField,
        tipAmount: {
          validate: tipAmt.validate,
          value: tipAmount,
          type: FieldTypes.TEXT_INPUT,
        } as FormField,
        postalOrZipCode: {
          // updates validation method for postal or zip depending on selected card country
          validate: validatePostalOrZipCode,
          value: postalOrZipCode,
          type: FieldTypes.TEXT_INPUT,
        } as FormField,
      }

      if (
        (tbPaymentType === TBPaymentType.PAYRIX || tbPaymentType === TBPaymentType.WORLDPAY) &&
        showOnlinePayment
      ) {
        onlinePaymentInfo.billingAddress = {
          validate: billingAddress.validate,
          value: streetAddress?.formattedAddress,
          type: FieldTypes.TEXT_INPUT,
        } as FormField
      }
    }

    if (!guestInfo) {
      contactInfo = {
        phoneNumber: {
          validate: phoneNum.validate,
          value: phoneNumber,
          type: FieldTypes.TEXT_INPUT,
        } as FormField,
        sendSMS: {
          value: sendSMS,
          type: FieldTypes.CHECKBOX,
        },
      }
    }

    // update form state on any field change
    setFormFields({
      paymentMethod: {
        validate: pmtMethod.validate,
        value: paymentMethod,
        type: FieldTypes.TEXT_INPUT,
      } as FormField,
      ...contactInfo,
      // empty object when payment method is not online
      ...onlinePaymentInfo,
    })
  }, [
    isOnlinePayment,
    showOnlinePayment,
    tbPaymentType,
    paymentMethod,
    initialForm,
    postalOrZipCode,
    phoneNumber,
    cardCountry,
    tipAmount,
    sendSMS,
    guestInfo,
    streetAddress,
  ])

  useEffect(() => {
    if (cart.items.length > 0 && offersLoyaltyRewards && consumerXRefID != null) {
      // update the CartLoyaltyStateRef since we need to evaluate rewards
      prevCartLoyaltyStateRef.current = {
        items: cart.items,
        isLoyaltyEnabled: offersLoyaltyRewards,
      }
      handleUserRewards()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items, offersLoyaltyRewards])

  // Init Payrix PayFields Success and Failure methods
  const payfieldsOnFailure = (res: PayFieldsErrorReponse): void => {
    setModalError(res.errors[0].msg)
  }

  const payfieldOnSuccess = (res: Partial<{ data?: { token: string }[] }>): void => {
    if (!res.data?.[0]?.token) {
      setModalError('There was a problem processing your payment - please try again later')
    } else {
      // Store the token and launch the payment process
      payrixSessionToken.current = res.data?.[0].token
      setShowProcessingModal(true)
    }
  }

  // Redraw Payrix Fields if payment methods change
  useEffect(() => {
    // Reload the payment fields if the payment method change back and froth between online and others
    if (
      tbPaymentType === TBPaymentType.PAYRIX &&
      paymentMethod === PaymentMethod.ONLINE &&
      payrixSDKLoaded
    ) {
      if (PayFields.appended) {
        PayFields.addFields()
      }
    }
  }, [tbPaymentType, paymentMethod, payrixSDKLoaded])

  // Update the Payrix Billing address when it changes
  useEffect(() => {
    const simpleCountryMap = (countryCode: string): string => {
      let mappedCountryCode = ''
      switch (countryCode) {
        case 'US':
          mappedCountryCode = 'USA'
          break

        case 'CA':
          mappedCountryCode = 'CAN'
          break
      }

      return mappedCountryCode
    }

    if (payrixSDKLoaded && streetAddress) {
      if (!['CA', 'US'].includes(streetAddress.country)) {
        setValidBillingAddress(false)
        PayFields.config.billingAddress = null
        return
      }

      PayFields.config.billingAddress = {
        address: streetAddress.address1,
        address2: streetAddress.address2 ?? '',
        city: streetAddress.city,
        country: simpleCountryMap(streetAddress.country),
      }

      switch (streetAddress.country) {
        case 'US':
          PayFields.config.billingAddress.state = streetAddress.state
          PayFields.config.billingAddress.zip = postalOrZipCode
          break

        case 'CA':
          PayFields.config.billingAddress.province = streetAddress.state
          PayFields.config.billingAddress.postalCode = postalOrZipCode
          break
      }
    }
  }, [payrixSDKLoaded, streetAddress, postalOrZipCode])

  // HANDLERS
  const handleContactChange = (
    hasSMS: boolean,
    phoneNum: string,
    marketingEmailConsent: boolean
  ): void => {
    setPhoneNumber(phoneNum)
    setSendSMS(hasSMS)
    setMarketingEmailConsent(marketingEmailConsent)
  }

  const handlePaymentDetailsChange = useDebounce((orderPayment: OrderPayment): void => {
    const needToUpdateCart =
      (paymentMethod != null && paymentMethod !== orderPayment.paymentMethod) ||
      tipAmount !== orderPayment.tipAmount
    setPaymentMethod(orderPayment.paymentMethod)

    if (orderPayment.paymentMethod === PaymentMethod.ONLINE) {
      setPostalOrZipCode(orderPayment.postalCode ?? '')
      setCardCountry(orderPayment.country === CardCountry.US ? CardCountry.US : CardCountry.CA)
      setTipAmount(orderPayment.tipAmount ?? '0.00')
    } else {
      setWePay(null)
    }

    // if isAddressRequired is true, this means the user landed on checkout
    // without delivery address we must prompt the user for one
    // else only update the cart if something cart update worthy has changed
    // (i.e. new payment method or tipAmout was changed)
    if (isAddressRequired) {
      handleOpenOrderMethodSelectorModal()
    } else if (needToUpdateCart) {
      handleUpdateMe({
        cart: {
          items: cart.items,
          tipAmount: orderPayment.tipAmount,
          orderingAddress: cart.orderingAddress,
          scheduledFor: cart.scheduledFor,
          orderMethod: cart.orderMethod,
        },
        paymentMethodData: orderPayment.paymentMethod,
        keepModalOpen: true,
      })
    }
  }, 250)

  const handleCheckoutError = (invalidFields: string[]): void => {
    invalidFields.forEach((fieldName: string) => {
      const isPostalOrZipCode = fieldName === 'postalOrZipCode'
      const isPhoneNum = fieldName === 'phoneNumber'
      const isBillingAddress = fieldName === 'billingAddress'

      if (isPostalOrZipCode) {
        if (paymentDetailsRef.current != null) {
          paymentDetailsRef.current.scrollIntoView({ behavior: 'smooth' })
        }
        setValidPostalOrZipCode(false)
      } else if (isPhoneNum && phoneNumberRef.current != null) {
        phoneNumberRef.current.scrollIntoView({ behavior: 'smooth' })
        setValidPhoneNumber(false)
      } else if (isBillingAddress) {
        setValidBillingAddress(false)
      }
    })
  }

  const tokenizeCard = async (): Promise<string> => {
    try {
      if (wePay != null) {
        return (await wePay.tokenize()).id
      }
      return ''
    } catch (err) {
      const tokenizedErrorMessage = err as TokenizeError[]
      const message = t(`${tokenizeErrorMessage(tokenizedErrorMessage)}`)
      setShowProcessingModal(false)
      openModal(
        <PopupModalTemplate
          name="payment-failed"
          title={t('error.modal.title_payment_failed')}
          callToAction={t('modal.submit')}
          message={message}
          onClose={closeModal}
        />
      )
      return '' // the errors are handled above, this return is just to appease TS
    }
  }

  const validateField = (field: FormField): boolean => {
    switch (field.type) {
      case FieldTypes.TEXT_INPUT:
        return field.validate != null ? field.validate(field.value) : true
      case FieldTypes.CHECKBOX:
        return field.validate != null ? field.validate(field.value) : true
      case FieldTypes.NUM_INPUT:
        return field.validate != null ? field.validate(field.value) : true
      default:
        // this line is unreachable but mandated by eslint
        return true
    }
  }

  const handleScheduleNextAvailableTime = async (
    nextSchedulingTimeslot: DateTime | null
  ): Promise<void> => {
    if (nextSchedulingTimeslot) {
      await handleUpdateMe({
        cart: {
          items: cart.items,
          orderingAddress: cart.orderingAddress,
          orderMethod: cart.orderMethod,
          scheduledFor: nextSchedulingTimeslot.toISO() ?? undefined,
        },
      })
      closeModal()
    }
  }

  const handleSubmit = (): Promise<void> | void => {
    const invalidFields: string[] = []
    // turn object keys into fieldname list
    const fieldsToValidate = showOnlinePayment
      ? Object.keys(formFields)
      : Object.keys(formFields).filter(
          (key) => key !== 'postalOrZipCode' && key !== 'cardCountry' && key !== 'billingAddress'
        )

    fieldsToValidate.forEach((fieldname: string) => {
      // lookup field value and validator by name
      const field = formFields[fieldname]
      // keep track of invalid fields
      if (!validateField(field)) {
        invalidFields.push(fieldname)
      }
    })

    const isFormValid = invalidFields.length === 0
    // expose invalid field names
    if (!isFormValid) {
      return handleCheckoutError(invalidFields)
    }

    if (isOnlinePayment && showOnlinePayment && tbPaymentType === TBPaymentType.WORLDPAY) {
      return handleHostedPayment()
    }

    // Only submit payfields when all above conditions are false
    if (
      payrixSDKLoaded &&
      isOnlinePayment &&
      showOnlinePayment &&
      tbPaymentType === TBPaymentType.PAYRIX
    ) {
      PayFields.onFailure = payfieldsOnFailure
      PayFields.onSuccess = payfieldOnSuccess

      return PayFields.submit()
    }

    // showing processing modal will ultimately trigger handleCheckout
    return setShowProcessingModal(true)
  }

  const handleOrderLimitReached = async (): Promise<void> => {
    if (isScheduled) {
      const nextSchedulingTime = await getNextSchedulingTime()
      openModal(
        <RescheduleModal
          onReschedule={handleOpenOrderMethodSelectorModal}
          selectNext={
            nextSchedulingTime
              ? {
                  onSelectNext: () => handleScheduleNextAvailableTime(nextSchedulingTime),
                  nextAvailable: nextSchedulingTime,
                }
              : undefined
          }
          message={t('order_throttling.reschedule_modal_message', {
            context: orderMethod.toLowerCase(),
          })}
          orderMethod={orderMethod}
        />,
        { forceSubmit: true, scrollBehavior: 'SCROLL', variant: 'BIG' }
      )
      return
    }
    if (isSchedulingAvailable) {
      openModal(
        <PopupModalTemplate
          name="order-limit-reached"
          title={t('error.modal.title_err_order_throttling_limit_reached')}
          callToAction={t('order_throttling.schedule_order_button')}
          icon={<SvgIcon fontSize="80px" name="alert" color="WARNING_ICON" />}
          onSubmit={() => handleOpenOrderMethodSelectorModal()}
          message={t('error.modal.message_err_order_throttling_limit_reached')}
          callToActionVariant={ButtonVariants.PRIMARY}
        />
      )
    } else {
      if (venue === null) {
        return
      }
      openModal(
        <PickupOnlyReachLimitModal
          venue={venue}
          confirmText={t('order_method_selector.go_back_view_menu')}
          onClose={async () => {
            await router.push(
              '/[orderMethod]/[venueXRefID]/menu',
              `/${orderMethod.toLowerCase()}/${venueXRefID}/menu`
            )
          }}
        />,
        {
          forceSubmit: true,
          scrollBehavior: 'SCROLL',
        }
      )
    }
  }

  const handleTimeslotOutsideAvailable = (
    scheduledFor: string,
    scheduledOrderMethod: OrderMethod
  ): void => {
    const scheduledForDT = DateTime.fromISO(scheduledFor)
    if (
      scheduledForDT.diffNow().milliseconds < 360 * 60 * 1000 &&
      scheduledForDT > DateTime.now()
    ) {
      openModal(
        <RescheduleModal
          onReschedule={handleOpenOrderMethodSelectorModal}
          orderMethod={scheduledOrderMethod}
          message={t('error.message_err_timeslot_no_longer_available', {
            time: scheduledForDT.toFormat('t').replace(/\./g, '').toUpperCase() ?? '',
          })}
        />,
        { forceSubmit: true, scrollBehavior: 'SCROLL', variant: 'BIG' }
      )
    } else {
      setModalError(ScheduledOrderErrorCode.VALIDATE_OUTSIDE_TIMESLOTS)
    }
  }

  const createOrderRequest = (orderPayment: OrderPayment): OrderRequest => {
    let notes = orderNotes
    if (includeCutlery) {
      notes = `Include Cutlery${orderNotes.length > 0 ? `\n${orderNotes}` : ''}`
    }

    const isGuest = guestInfo && !userSession.isSignedIn

    const orderRequest: OrderRequest = {
      providerID:
        cart.delivery?.fleet === DeliveryFleet.DOORDASH
          ? OrderProvider.TOUCHBISTRO_DOORDASH
          : OrderProvider.TOUCHBISTRO,
      phone: (isGuest ? guestInfo.phone : phoneNumber).replace(/\D+/g, ''),
      orderMethod,
      items: dehydrateCartItems(cart.items),
      smsNotification: isGuest ? guestInfo.smsConsent : sendSMS,
      locale: userLanguage,
      isScheduled,
      notes,
      ...orderPayment,
      loyaltyTransactionXRefID: loyaltySession?.loyaltyTransactionXRefID ?? undefined,
      selectedRewardXRefID: loyaltySession?.selectedRewardXRefID ?? undefined,
      loyaltyDiscountTotal:
        userRewards?.rewards?.find(
          (reward) => reward.rewardXRefID === loyaltySession?.selectedRewardXRefID
        )?.discountAmount ?? undefined,
      selectedGiftCardCode: loyaltySession?.selectedGiftCardCode,
      recipientInformation: isGuest
        ? { firstName: guestInfo.firstName, lastName: guestInfo.lastName, email: guestInfo.email }
        : undefined,
    }

    if (marketingEmailConsent) {
      orderRequest.marketingEmailConsent = marketingEmailConsent
    }

    if (isScheduled) {
      orderRequest.scheduledFor = cart.scheduledFor
    }

    const orderRequestBody: OrderRequest = orderRequest

    // PII; only add ordering address if orderMethod is delivery
    if (cart.orderMethod === OrderMethod.delivery && cart.orderingAddress != null) {
      orderRequestBody.orderingAddress = {
        ...cart.orderingAddress,
        saveDeliveryAddress,
      }
    }

    return orderRequestBody
  }

  const handleRequestOrder = async (orderRequest: OrderRequest): Promise<void> => {
    try {
      const processedOrder = await requestOrder(venueXRefID, orderRequest)

      let marketingConsentStatus
      if (showMarketingConsent && (orderRequest.marketingEmailConsent ?? false)) {
        marketingConsentStatus = 'NEW_CONSENT'
      } else if (showMarketingConsent && !(orderRequest.marketingEmailConsent ?? false)) {
        marketingConsentStatus = 'NO_CONSENT_GIVEN'
      } else if (!showMarketingConsent && (marketingConsent?.emailConsent ?? false)) {
        marketingConsentStatus = 'ALREADY_CONSENTED'
      } else {
        marketingConsentStatus = 'NO_CONSENT_AVAILABLE'
      }

      // Reset ecommerce and send a purchase event
      window.dataLayer?.push({ ecommerce: null })
      window.dataLayer?.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: processedOrder.orderXRefID,
          value: Number(processedOrder.bill.grandTotal),
          tax: Number(processedOrder.bill.totalTax),
          shipping:
            processedOrder.orderMethod === OrderMethod.delivery
              ? processedOrder.bill.serviceCharges
                  .filter((serviceCharge) => serviceCharge.type === ServiceChargeType.DELIVERY_FEE)
                  .reduce(
                    (deliveryFee, deliveryServiceCharge) =>
                      deliveryFee + Number(deliveryServiceCharge.totalAmount),
                    0
                  )
              : 0,
          is_guest: !profile,
          marketing_email_consent: marketingConsentStatus,
          currency: venue?.venueCurrency ?? getCurrencyByCountry(venue?.venueCountry ?? ''),
          items: processedOrder.items.map((item) => ({
            item_id: item.itemID,
            item_name: item.name,
            item_list_id: item.menuGroupXRefID,
            item_list_name: operationalMenu.menuGroups[item.menuGroupXRefID]?.label,
            price: Number(item.price),
            quantity: Number(item.quantity),
          })),
        },
      })
      if (guest) {
        // Scroll to top of screen
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Format delivery address if it exists
        if (processedOrder.deliveryAddress != null) {
          processedOrder.deliveryAddress.formattedAddress = formatAddress(
            processedOrder.deliveryAddress
          )
        }
        setProcessedGuestOrder(processedOrder)
      } else {
        await router.push(
          '/[orderMethod]/[venueXRefID]/[orderXRefID]',
          `/${orderMethod.toLowerCase()}/${venueXRefID}/${processedOrder.orderXRefID}`
        )
      }
    } catch (requestOrderError) {
      const { code } = parseRequestError(requestOrderError)
      composeCartError(requestOrderError, {
        onLoyaltyError: () => {
          setModalError(requestOrderError, () => {
            router.replace(router.asPath, undefined, {
              scroll: false,
            })
          })
        },
        onStaleError: () => {
          setModalError(StaleCartErrorCode.MODIFIER_GENERIC)
          router.replace(router.asPath, undefined, {
            scroll: false,
          })
        },
        onThrottlingLimitReached: handleOrderLimitReached,
        onOutsideTimeslots: () => {
          if (cart.scheduledFor) {
            handleTimeslotOutsideAvailable(cart.scheduledFor, orderMethod)
          } else {
            setModalError(code)
          }
        },
        onDefaultFallback: () => {
          setModalError(code)
        },
      })
      setShowProcessingModal(false)

      datadogRum.addAction('place-order', {
        items: orderRequest.items,
        venueXRefID,
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleHostedPayment = async (): Promise<void> => {
    const orderPayment: OrderPayment | null = paymentMethod != null ? { paymentMethod } : null
    if (orderPayment == null) {
      return
    }

    orderPayment.tipAmount = tipAmount

    if (venue && cart.bill.outstandingAmount) {
      const returnURL = `${
        publicConfig.baseURL
      }/${orderMethod.toLowerCase()}/${venueXRefID}/paymentstatus`
      const dateTime = cart.scheduledFor
        ? t('checkout.payment.hosted_payments_order_details_scheduled_for', {
            value: DateTime.fromISO(cart.scheduledFor).toLocaleString(DateTime.DATETIME_MED),
          })
        : ''
      const orderDetails = `${
        cart.scheduledFor ? OrderTime.SCHEDULED.toUpperCase() : OrderTime.ASAP.toUpperCase()
      } ${orderMethod.toUpperCase()}${dateTime}`

      let transaction: TransactionSetupResponse
      try {
        transaction = await transactionSetup(venue.venueXRefID, {
          amount: cart.bill.outstandingAmount,
          returnURL,
          address: {
            address1: userSession.billingAddress?.address1 ?? '',
            address2: userSession.billingAddress?.address2 ?? '',
            city: userSession.billingAddress?.city ?? '',
            state: userSession.billingAddress?.state ?? '',
            zipcode: userSession.billingAddress?.zipcode ?? '',
          },
          companyName: venue.name,
          logoURL: buildAssetsPath('/images/tb-logo-small.png'),
          tagline: t('checkout.payment.hosted_payment_tag_line', { value: venue.name }),
          welcomeMessage: t('checkout.payment.hosted_payment_welcome_message'),
          processTransactionTitle: t('checkout.payment.hosted_payment_process_transaction_title'),
          duplicateCheckDisableFlag: true,
          orderDetails,
        })
        const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

        const { items, orderMethod, orderingAddress, scheduledFor } = cart

        const orderRequest = createOrderRequest(orderPayment)
        const UpdateMeRequest = {
          cart: {
            items: (items ?? []).map(mapItemsToCartRequest),
            orderMethod,
            orderingAddress: orderMethod !== OrderMethod.delivery ? undefined : orderingAddress,
            scheduledFor,
            discountDetails: cart.discountDetails,
          },
          selectedRewardXRefID: orderRequest.selectedRewardXRefID,
          giftCardCode: orderRequest.selectedGiftCardCode,
          guestInfo,
          orderRequest,
          promoCodes: prevPromoCodes,
        }
        await updateMe(venueXRefID, UpdateMeRequest)

        if (venue?.venueIsTest && hasOODevFF) {
          router.push(
            `${publicConfig.hostedPaymentTestURL}/?TransactionSetupID=${transaction.transactionSetupID}`
          )
        } else {
          router.push(
            `${publicConfig.hostedPaymentProdURL}/?TransactionSetupID=${transaction.transactionSetupID}`
          )
        }
      } catch (e) {
        const axiosOrderError = parseRequestError(e)
        setModalError(axiosOrderError)
      }
    }
  }

  const handleCheckout = async (): Promise<void> => {
    const orderPayment: OrderPayment | null = paymentMethod != null ? { paymentMethod } : null
    if (orderPayment == null) {
      return
    }
    if (isOnlinePayment && (wePay != null || payrixSession)) {
      // tokenize card only once on checkout when online payment is selected and iframe is rendered
      if (showOnlinePayment) {
        let paymentCardToken = ''
        if (wePay != null) {
          paymentCardToken = await tokenizeCard()
        }

        if (payrixSession && payrixSDKLoaded) {
          paymentCardToken = payrixSessionToken.current
        }

        if (paymentCardToken === '') {
          return
        }
        orderPayment.country = cardCountry
        orderPayment.paymentCardToken = paymentCardToken
        orderPayment.postalCode = trimAllWhitespace(postalOrZipCode)
      }
      orderPayment.tipAmount = tipAmount
    }
    const orderRequest = createOrderRequest(orderPayment)
    handleRequestOrder(orderRequest)
  }

  const handleShowVenueClosedModal = async (): Promise<void> => {
    await router.push(
      '/[orderMethod]/[venueXRefID]/menu',
      `/${orderMethod.toLowerCase()}/${venue?.venueXRefID}/menu`
    )
  }

  const handleChangeOrderNotes = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const { value } = e.target
    setOrderNotes(value == null ? '' : value.slice(0, ORDER_NOTES_MAX_LENGTH))
  }

  const handleSelectGiftCard = async (giftCardCode: string | null): Promise<void> => {
    if (consumerXRefID == null) {
      return
    }
    const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

    try {
      setMe((previousState) => ({
        ...previousState,
        loyaltySession: {
          ...previousState.loyaltySession,
          isLoyaltySessionLoading: true,
        },
        cart: { ...previousState.cart, isCartLoading: true },
      }))

      const meRequest: UpdateMeRequest = {
        cart: {
          orderMethod: cart.orderMethod ?? OrderMethod.pickup,
          items: (cart.items ?? []).map(mapItemsToCartRequest),
          orderingAddress:
            cart.orderMethod !== OrderMethod.delivery ? undefined : cart.orderingAddress,
          scheduledFor: cart.scheduledFor,
          tipAmount: applyTip ? tipAmount : undefined,
        },
        giftCardCode,
        promoCodes: prevPromoCodes,
      }

      const me = await updateMe(venueXRefID, meRequest)

      setMe((previousState) => ({
        loyaltySession: {
          ...previousState.loyaltySession,
          ...me.loyaltySession,
          selectedGiftCardCode: giftCardCode,
          isLoyaltySessionLoading: false,
        },
        cart: {
          ...previousState.cart,
          ...me.cart,
          isCartLoading: false,
        },
      }))
    } catch (updateMeError) {
      setModalError(updateMeError)

      setMe((previousState) => ({
        loyaltySession: {
          ...previousState.loyaltySession,
          isLoyaltySessionLoading: false,
        },
        cart: { ...previousState.cart, isCartLoading: false },
      }))
    }
  }

  const handleAddGiftCard = async ({ code, recaptchaToken }: GiftCardRequest): Promise<void> => {
    if ((consumerXRefID == null && guest === null) || cart == null) {
      return
    }

    try {
      setMe((previousState) => ({
        ...previousState,
        cart: { ...previousState.cart, isCartLoading: true },
      }))

      // we're checking that the newly added gift card is not already added
      // if it is, simply select it
      setDigitalGiftCardInputError(null)
      const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

      const meRequest: UpdateMeRequest = {
        cart: {
          orderMethod: cart.orderMethod ?? OrderMethod.pickup,
          items: (cart.items ?? []).map(mapItemsToCartRequest),
          orderingAddress:
            cart.orderMethod !== OrderMethod.delivery ? undefined : cart.orderingAddress,
          scheduledFor: cart.scheduledFor,
          tipAmount: applyTip ? tipAmount : undefined,
        },
        // to select a gift card
        giftCardCode: code,
        promoCodes: prevPromoCodes,
      }

      // to add a gift card
      if (!giftCards?.some((card) => card.code === code)) {
        meRequest.code = code
        meRequest.recaptchaToken = recaptchaToken
      }

      const me = await updateMe(venueXRefID, meRequest)

      // the new added gift card code should be updated and pass to the order service
      setMe((previousState) => ({
        loyaltySession: {
          ...previousState.loyaltySession,
          ...me.loyaltySession,
          selectedGiftCardCode: code,
          isLoyaltySessionLoading: false,
        },
        cart: {
          ...previousState.cart,
          ...me.cart,
          isCartLoading: false,
        },
      }))
    } catch (updateMeError) {
      const parsedError = parseRequestError(updateMeError)
      if (parsedError.code === LoyaltyRewardErrorCode.GiftCardInvalidOrSpent) {
        setDigitalGiftCardInputError(t('checkout.payment.invalid_gift_card'))
      } else {
        setModalError(parsedError)
      }
      setMe((previousState) => ({
        ...previousState,
        cart: { ...previousState.cart, isCartLoading: false },
      }))
    }
  }

  const handleClearCart = async (): Promise<void> => {
    await handleUpdateMe({
      cart: {
        items: [],
        orderMethod: cart.orderMethod,
        orderingAddress: cart.orderingAddress,
        scheduledFor: cart.scheduledFor,
      },
      ...(cart.bill.giftCardPayments != null &&
        cart.bill.giftCardPayments.length > 0 &&
        venueXRefID != null &&
        profile?.consumerXRefID != null && { giftCardCode: null }),
    })
    setUserRewards(null)
  }

  const handleUpdateGuestInfo = async (guestInformation: GuestInformation): Promise<void> => {
    const { items, scheduledFor, orderingAddress, orderMethod } = cart
    closeModal()
    await handleUpdateMe({
      guestInformation,
      cart: {
        items,
        scheduledFor,
        orderingAddress,
        orderMethod,
        tipAmount: applyTip ? tipAmount : undefined,
      },
    })
    setGuestInfo(guestInformation)
  }

  const renderVenueNotice = (): ReactNode => {
    const { notice } = getOrderMethodSettings(venue?.venueOrderingMethods ?? [], orderMethod)
    if (notice.length === 0) {
      return null
    }

    return (
      <Grid m="12px 0 0 0">
        <Text m="0 0 6px 0" type="H4">
          {t('checkout.notice.title', { context: orderMethod.toLowerCase() })}
        </Text>
        {notice
          .split(/\n/g)
          // remove newline chars we split by; btw they dont show in chrome console
          .filter((line: string) => line.length !== 1)
          .map((line: string) => (
            <StyledText key={line}>{line.replace(/\\/g, '')}</StyledText>
          ))}
      </Grid>
    )
  }

  const renderOrderTime = (): ReactNode => {
    if (isScheduled && cart.scheduledFor != null) {
      return (
        <Grid gap="10px" data-test="checkout-scheduled-for">
          <Text type="H4">{t('checkout.scheduled_for.title')}</Text>
          {cart.isCartLoading ? (
            <PulsateLoader width="60px" height="18px" />
          ) : (
            <Text>
              {t('date', {
                value: DateTime.fromISO(cart.scheduledFor).toISODate(),
              })}{' '}
              -{' '}
              {t('time', {
                value: DateTime.fromISO(cart.scheduledFor),
              })}
            </Text>
          )}
        </Grid>
      )
    }

    return (
      <Grid gap="10px">
        <Text type="H4">
          {t('checkout.waittime.title', { context: orderMethod.toLowerCase() })}
        </Text>
        <Text>{`${t('checkout.waittime.body.minutes', { waitTime })}`}</Text>
      </Grid>
    )
  }

  const handleAddPromoCode = async (promoCode: string): Promise<void> => {
    const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

    if (promoCode && promoCode.length > 0 && !prevPromoCodes.includes(promoCode)) {
      if ((consumerXRefID == null && guest === null) || cart == null) {
        return
      }

      await handleUpdateMe({
        cart: {
          orderMethod: cart.orderMethod ?? OrderMethod.pickup,
          items: (cart.items ?? []).map(mapItemsToCartRequest),
          orderingAddress:
            cart.orderMethod !== OrderMethod.delivery ? undefined : cart.orderingAddress,
          scheduledFor: cart.scheduledFor,
          tipAmount,
        },
        promoCodes: [...prevPromoCodes, promoCode.toUpperCase()],
      })
    }
  }

  const handleRemovePromoCode = async (promoCode: string): Promise<void> => {
    if ((consumerXRefID == null && guest === null) || cart == null) {
      return
    }

    const prevPromoCodes = cart.discountDetails ? cart.discountDetails.promoCodes ?? [] : []

    await handleUpdateMe({
      cart: {
        orderMethod: cart.orderMethod ?? OrderMethod.pickup,
        items: (cart.items ?? []).map(mapItemsToCartRequest),
        orderingAddress:
          cart.orderMethod !== OrderMethod.delivery ? undefined : cart.orderingAddress,
        scheduledFor: cart.scheduledFor,
        tipAmount: applyTip ? tipAmount : undefined,
      },
      promoCodes: prevPromoCodes.filter((pC) => pC !== promoCode),
    })
  }

  // FINALLY RETURNS
  if (!userSession.isInitialized) {
    return <LoadScreen />
  }

  const backToMenu = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault()
    await router.push(
      '/[orderMethod]/[venueXRefID]/menu',
      `/${orderMethod.toLowerCase()}/${venueXRefID}/menu`
    )
  }

  if (venue == null) {
    return null
  }

  return (
    <div className={checkoutTemplateStyles(theme)}>
      <DefaultThemeProvider>
        {/* Loads WePay SDK */}
        <Script src="https://cdn.wepay.com/wepay.min.js" />
        {/* Loads Payrix SDK */}
        <Script src="https://code.jquery.com/jquery-2.2.4.min.js" />
        {/* <Script src="https://test-api.payrix.com/payFieldsScript"  /> */}
        <Header
          variant={HeaderVariant.CHECKOUT}
          userProfile={profile ?? undefined}
          cart={{
            isCartLoading: cart.isCartLoading,
            orderMethod,
            delivery: cart.delivery,
            orderingAddress: cart.orderingAddress,
            scheduledFor: cart.scheduledFor,
          }}
          venueXRefID={venueXRefID}
        />
        <div ref={payrixScriptRef}></div>
      </DefaultThemeProvider>
      <main className={checkoutPageBodyLayout(theme)} data-test="checkout-page">
        <EditItemProviderWithNestMods
          operationalMenu={operationalMenu}
          hasNestedMods={shouldShowNestedMods}
        >
          <div className={contentStyles(theme)}>
            <StyledButton variant="LINK" onClick={backToMenu} type="button">
              <StyledSvgIcon name="chevron_left" fontSize="18px" color="PRIMARY" />
              {t('checkout.menu_button')}
            </StyledButton>

            <Text type="H1">{`${venue.name} ${t('checkout.header.title')}`}</Text>
            {venue.active ? (
              <form data-test="checkout-form">
                <Text>{t('checkout.header.disclaimer')}</Text>
                <Divider classNames={dividerStyles} />
                <section className={contactSectionStyles} data-test="contact-information-section">
                  {userSession.isSignedIn ? (
                    <ContactInfo
                      ref={phoneNumberRef}
                      onChange={handleContactChange}
                      isValidPhoneNumber={validPhoneNumber}
                      profile={profile}
                      phoneNumber={phoneNumber}
                      sendSMS={sendSMS}
                      marketingEmailConsent={marketingEmailConsent}
                      showMarketingEmailConsent={showMarketingConsent}
                      marketingGroupName={venue.parentGroupName ?? ''}
                    />
                  ) : (
                    !!guestInfo && (
                      <GuestInfo
                        guest={guestInfo}
                        handleUpdateMe={handleUpdateGuestInfo}
                        venue={venue}
                        orderMethod={orderMethod}
                      />
                    )
                  )}
                </section>
                <Divider classNames={dividerStyles} />
                <section data-test="venue-ordering-details" className={venueOrderingStyles}>
                  <LocationDetails
                    openOrderMethodSelector={handleOpenOrderMethodSelectorModal}
                    venue={venue}
                    orderingAddress={cart.orderingAddress}
                    deliveryFleet={cart.delivery?.fleet}
                    onSaveDeliveryAddressChanged={() => {
                      // checkbox was clicked, flip its value
                      setSaveDeliveryAddress(!saveDeliveryAddress)
                    }}
                    saveDeliveryAddress={saveDeliveryAddress}
                    savedAddresses={deliveryAddresses}
                    isCartLoading={cart?.isCartLoading}
                    consumerPhoneNumber={profile?.phone}
                    orderMethod={orderMethod}
                  />
                  {renderOrderTime()}
                  {renderVenueNotice()}
                  <label htmlFor="order-notes" css={[formStyles(theme).label]}>
                    {t('checkout.notes.label', {
                      context: orderMethod.toLowerCase(),
                    })}
                    <TextField
                      variant={InputVariant.INPUT}
                      onChange={handleChangeOrderNotes}
                      data-test="order-notes"
                      id="order-notes"
                      value={orderNotes}
                      maxLength={ORDER_NOTES_MAX_LENGTH}
                    />
                  </label>
                </section>
                <Divider classNames={dividerStyles} />
                <div className={formWrapperStyles}>
                  {/** render cart widget as form section on smaller breakpoints */}
                  {windowSize.width < theme.breakpointValues.SM && (
                    <section className={cartBodyStyles} data-test="cart-widget-form-section">
                      {cart.items.length > 0 && isCartAvailable && (
                        <CartHeader
                          cart={{
                            bill: cart.bill,
                            isCartLoading: cart.isCartLoading,
                            orderMethod,
                          }}
                          variant={CartWidgetVariant.CHECKOUT}
                          onClearCart={handleClearCart}
                          venueXRefID={venueXRefID}
                        />
                      )}
                      <CartBody
                        venue={{
                          isASAPAvailable,
                          isSchedulingAvailable,
                          venueReopensAt,
                          disableTip: venue.disableTip,
                          includeCutlery: venue.disableCutlery ? undefined : includeCutlery,
                          active: venue.active,
                          name: venue.name,
                          orderThrottlingLimitReached,
                          venueOrderingMethods: venue.venueOrderingMethods,
                        }}
                        cart={{
                          bill: cart.bill,
                          delivery: cart.delivery,
                          isCartLoading: cart.isCartLoading,
                          scheduledFor: cart.scheduledFor,
                          items: cart.items,
                          appliedLoyaltyRewardName: cart.loyaltyRewardApplied?.name,
                          appliedLoyaltyRewardAmount: cart.loyaltyRewardApplied?.discountAmount,
                          orderMethod,
                          discountDetails: cart.discountDetails,
                        }}
                        variant={CartWidgetVariant.CHECKOUT}
                        onItemsChange={handleCartItemsChange}
                        onChangeCutlery={setIncludeCutlery}
                        userRewards={userRewards}
                        loyaltySession={loyaltySession}
                        onSelectReward={handleSelectReward}
                        offersLoyaltyRewards={offersLoyaltyRewards}
                        onClearLoyalty={handleClearLoyalty}
                        loyaltyConfig={loyaltyConfig}
                        operationalMenu={operationalMenu}
                        currency={payrixSessionCurrency}
                        hasAdvancedDiscounts={hasAdvancedDiscounts}
                        applyPromoCodeError={promoCodeInputError}
                        promoCodeHandlers={{
                          promoCodes: cart.discountDetails ? cart.discountDetails.promoCodes : [],
                          addPromoCodeHandler: handleAddPromoCode,
                          removePromoCodeHandler: handleRemovePromoCode,
                        }}
                      />
                      <aside
                        className={`sticky-place-order-wrapper ${stickyPlaceOrderButtonStyles(
                          theme
                        )}`}
                      >
                        <Button
                          data-test="sticky-place-order-button"
                          type="button"
                          onClick={handleSubmit}
                          disabled={!venue.active}
                          icon={isPaymentRedirectNoteVisible ? 'external_link' : undefined}
                          center={isPaymentRedirectNoteVisible}
                        >
                          {isPaymentRedirectNoteVisible
                            ? t('cart_widget.footer.button_redirect')
                            : t('cart_widget.footer.button_checkout')}
                        </Button>
                        {isPaymentRedirectNoteVisible && (
                          <Text m="10px 0" data-test="redirect-note">
                            {t('cart_widget.footer.redirect_note')}
                          </Text>
                        )}
                      </aside>
                      <Divider classNames={dividerStyles} />
                    </section>
                  )}
                  <section className={paymentSectionStyles} data-test="payment-details-section">
                    <PaymentDetails
                      ref={paymentDetailsRef}
                      paymentMethods={paymentMethods}
                      onChange={handlePaymentDetailsChange}
                      providerXRefID={
                        isDelivery && cart.delivery?.fleet === DeliveryFleet.DOORDASH
                          ? OrderProvider.TOUCHBISTRO_DOORDASH
                          : OrderProvider.TOUCHBISTRO
                      }
                      isValid={validPostalOrZipCode}
                      setWePay={setWePay}
                      wePayIframe={wePay}
                      isScheduled={isScheduled}
                      isOODev={hasOODevFF}
                      isDGCEnabled={loyaltyConfig?.config?.giftCardsEnabled ?? false}
                      disableTip={venue.disableTip}
                      isCartLoading={cart.isCartLoading}
                      attachedGiftCards={giftCards}
                      selectedGiftCardCode={loyaltySession?.selectedGiftCardCode}
                      handleAddGiftCard={handleAddGiftCard}
                      applyGiftCardError={digitalGiftCardInputError}
                      handleSelectGiftCard={handleSelectGiftCard}
                      showOnlinePayment={showOnlinePayment}
                      venueCountry={venue.venueCountry}
                      isEmptyCart={cart.items.length === 0}
                      totalAfterTax={cart.bill.totalAfterTax}
                      streetAddress={streetAddress}
                      setStreetAddress={handleSetStreetAddress}
                      setZipCode={setPostalOrZipCode}
                      zipCode={postalOrZipCode}
                      validBillingAddress={validBillingAddress && validPostalOrZipCode}
                      setValidZipCode={setValidPostalOrZipCode}
                      setValidBillingAddress={setValidBillingAddress}
                      tbPaymentType={tbPaymentType}
                      deliveryAddress={cart.orderingAddress}
                      orderMethod={orderMethod}
                      payrixSDKLoaded={payrixSDKLoaded}
                      venueBranding={venue.branding}
                    />
                    <Divider classNames={dividerStyles} />
                  </section>
                </div>
              </form>
            ) : (
              <div className={venueInactiveSection} data-test="venue-inactive">
                <Text type="H3" m="15px 0" align="center">
                  {t('checkout.venue_inactive_header')}
                </Text>
                <Text align="center">
                  {t('checkout.venue_inactive_message', {
                    venueName: venue.name,
                  })}
                </Text>
              </div>
            )}
          </div>
          {windowSize.width > theme.breakpointValues.SM && (
            <section className={cartWidgetSectionStyles}>
              <CartWidget
                cart={{
                  bill: {
                    ...cart.bill,
                    tipTotal: paymentMethod === PaymentMethod.ONLINE ? tipAmount : '0.00',
                  },
                  delivery: cart.delivery,
                  isCartLoading: cart.isCartLoading,
                  scheduledFor: cart.scheduledFor,
                  items: cart.items,
                  appliedLoyaltyRewardName: cart.loyaltyRewardApplied?.name,
                  appliedLoyaltyRewardAmount: cart.loyaltyRewardApplied?.discountAmount,
                  discountedOrderItemID: cart.discountedOrderItemID,
                  orderMethod,
                  discountDetails: cart.discountDetails,
                }}
                variant={CartWidgetVariant.CHECKOUT}
                onItemsChange={handleCartItemsChange}
                onSubmit={handleSubmit}
                setIncludeCutlery={setIncludeCutlery}
                offersLoyaltyRewards={offersLoyaltyRewards}
                userRewards={userRewards}
                onClearLoyalty={handleClearLoyalty}
                loyaltySession={loyaltySession}
                onSelectReward={handleSelectReward}
                onClearCart={handleClearCart}
                hasBoxShadow
                loyaltyConfig={loyaltyConfig}
                venue={{
                  isASAPAvailable,
                  isSchedulingAvailable,
                  venueReopensAt,
                  // tip is only shown on checkout as well
                  disableTip: venue.disableTip,
                  // we only show cutlery selection on checkout variant
                  includeCutlery: venue.disableCutlery ? undefined : includeCutlery,
                  active: venue.active,
                  venueOrderingMethods: venue.venueOrderingMethods,
                  name: venue.name,
                  venueXRefID,
                }}
                operationalMenu={operationalMenu}
                tbPaymentType={tbPaymentType}
                paymentMethod={paymentMethod}
                currency={payrixSessionCurrency}
                hasAdvancedDiscounts={hasAdvancedDiscounts}
                applyPromoCodeError={promoCodeInputError}
                promoCodeHandlers={{
                  promoCodes: cart.discountDetails ? cart.discountDetails.promoCodes : [],
                  addPromoCodeHandler: handleAddPromoCode,
                  removePromoCodeHandler: handleRemovePromoCode,
                }}
              />
            </section>
          )}
          {shouldShowNestedMods ? (
            <EditItemModalWithNestedMods
              operationalMenu={operationalMenu}
              venue={venue}
              isASAPAvailable={isASAPAvailable}
              isSchedulingAvailable={isSchedulingAvailable}
              isActive={venue.active}
              cart={cart}
              onUpdateMe={handleUpdateMe}
            />
          ) : (
            <EditItemModal
              operationalMenu={operationalMenu}
              venue={venue}
              isASAPAvailable={isASAPAvailable}
              isSchedulingAvailable={isSchedulingAvailable}
              isActive={venue.active}
              cart={cart}
              onUpdateMe={handleUpdateMe}
            />
          )}
        </EditItemProviderWithNestMods>
      </main>

      {/* MODALS */}
      <ProcessingModal onConfirm={handleCheckout} open={showProcessingModal} />

      <VenueClosedModal
        onClose={handleShowVenueClosedModal}
        venueReopensAt={venueReopensAt}
        open={!isASAPAvailable && !isScheduled}
      />
      <DefaultThemeProvider>
        <Footer />
      </DefaultThemeProvider>
    </div>
  )
}

export default Checkout
